import React, { useState, useEffect } from 'react';
import { Mic, MicOff } from 'lucide-react';

const SpeechRecognizer = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [language, setLanguage] = useState('en-US');
  const [recognition, setRecognition] = useState(null);

  useEffect(() => {
    // Check if the browser supports the Web Speech API
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Your browser does not support the Web Speech API. Please try Chrome or Edge.');
      return;
    }

    // Initialize the SpeechRecognition object
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognitionInstance = new SpeechRecognition();
    
    // Configure the recognition
    recognitionInstance.continuous = true;
    recognitionInstance.interimResults = true;
    recognitionInstance.lang = language;

    // Event handlers
    recognitionInstance.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      
      setTranscript(finalTranscript || interimTranscript);
    };

    recognitionInstance.onerror = (event) => {
      console.error('Speech recognition error', event.error);
      setIsListening(false);
    };

    recognitionInstance.onend = () => {
      if (isListening) {
        recognitionInstance.start();
      }
    };

    setRecognition(recognitionInstance);

    // The toggleListening reference depends on recognition and isListening 
    // so we need to define it inside the useEffect
    const handleToggleListening = () => {
      if (recognitionInstance) {
        if (isListening) {
          recognitionInstance.stop();
          setIsListening(false);
        } else {
          setTranscript('');
          recognitionInstance.start();
          setIsListening(true);
        }
      }
    };

    // Handle key press events
    const handleKeyDown = (e) => {
      // Check if space key is pressed and no input field is focused
      if (e.code === 'Space' && 
          document.activeElement.tagName !== 'INPUT' && 
          document.activeElement.tagName !== 'TEXTAREA') {
        e.preventDefault();
        handleToggleListening();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (recognitionInstance) {
        recognitionInstance.stop();
      }
    };
  }, [isListening, language]);

  useEffect(() => {
    if (recognition) {
      recognition.lang = language;
    }
  }, [language, recognition]);

  const toggleListening = () => {
    if (recognition) {
      if (isListening) {
        recognition.stop();
        setIsListening(false);
      } else {
        setTranscript('');
        recognition.start();
        setIsListening(true);
      }
    }
  };

  return (
    <div className="p-6 max-w-lg mx-auto bg-white rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800">Speech Recognizer</h2>
        <div className="flex items-center">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="mr-4 p-2 border border-gray-300 rounded"
          >
            <option value="en-US">English</option>
            <option value="de-DE">German</option>
          </select>
          <button
            onClick={toggleListening}
            className={`p-2 rounded-full ${isListening ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}
            title={isListening ? 'Stop listening (Space)' : 'Start listening (Space)'}
          >
            {isListening ? <MicOff size={24} /> : <Mic size={24} />}
          </button>
        </div>
      </div>
      
      <div className="mb-2 text-sm text-gray-600">
        Press <kbd className="px-2 py-1 bg-gray-200 rounded">Space</kbd> to {isListening ? 'stop' : 'start'} recording
      </div>
      
      <div className="mt-4">
        <div className="p-3 min-h-32 bg-gray-50 rounded border border-gray-200">
          {transcript ? transcript : 
            <span className="text-gray-400">
              {isListening ? 'Listening... Speak now' : 'Press Space or click the microphone to start speaking'}
            </span>
          }
        </div>
      </div>
      
      {isListening && (
        <div className="mt-2 flex justify-center">
          <div className="animate-pulse text-red-500">Recording...</div>
        </div>
      )}
    </div>
  );
};

export default SpeechRecognizer;